import { useMemo } from 'react'
import { useTheme } from 'styled-components'

import { ChartEntry, DotWithLabel, formatNumber, resolveOverlappingDots } from './helpers'

const OVERLAPPING_THRESHOLD = 60
const HORIZONTAL_OFFSET = 32
const VERTICAL_OFFSET = 16

// ChartEntry allows null (for gap-filled data), but this hook only receives raw data with real values
interface RawChartEntry extends ChartEntry {
  tvl: number
  apy: number
}

/**
 * Finds min/max entries for TVL and APY, maps them to pixel positions using the
 * provided scales, and resolves label overlaps so they don't collide on the chart.
 * Returns positioned ReferenceDot descriptors ready for rendering.
 */
export const useMinMaxDots = ({
  data,
  xScale,
  yScales,
  svgWidth,
  svgHeight,
  marginLeft,
  marginRight,
}: {
  data: RawChartEntry[]
  xScale: (val: number) => number
  yScales: Record<string, { scale: (val: number) => number }>
  svgWidth: number
  svgHeight: number
  marginLeft: number
  marginRight: number
}): DotWithLabel[] => {
  const theme = useTheme()

  return useMemo(() => {
    if (data.length === 0) return []
    const minTvl = data.reduce((min, d) => (d.tvl < min.tvl ? d : min), data[0])
    const maxTvl = data.reduce((max, d) => (d.tvl > max.tvl ? d : max), data[0])
    const minApy = data.reduce((min, d) => (d.apy < min.apy ? d : min), data[0])
    const maxApy = data.reduce((max, d) => (d.apy > max.apy ? d : max), data[0])

    const dots: DotWithLabel[] = [
      {
        entry: minTvl,
        x: xScale(minTvl.timestampMs),
        y: yScales.tvl.scale(minTvl.tvl),
        color: theme.colors.button.background,
        yAxisId: 'left',
        value: formatNumber(minTvl.tvl),
        label: 'Min',
        position: 'bottom',
      },
      {
        entry: maxTvl,
        x: xScale(maxTvl.timestampMs),
        y: yScales.tvl.scale(maxTvl.tvl),
        color: theme.colors.button.background,
        yAxisId: 'left',
        value: formatNumber(maxTvl.tvl),
        label: 'Max',
        position: 'top',
      },
      {
        entry: minApy,
        x: xScale(minApy.timestampMs),
        y: yScales.apy.scale(minApy.apy),
        color: theme.colors.text.danger2,
        yAxisId: 'right',
        value: `${(minApy.apy * 100).toFixed(2)}%`,
        label: 'Min',
        position: 'bottom',
      },
      {
        entry: maxApy,
        x: xScale(maxApy.timestampMs),
        y: yScales.apy.scale(maxApy.apy),
        color: theme.colors.text.danger2,
        yAxisId: 'right',
        value: `${(maxApy.apy * 100).toFixed(2)}%`,
        label: 'Max',
        position: 'top',
      },
    ]

    return resolveOverlappingDots({
      dots,
      svgWidth,
      svgHeight,
      margin: { left: marginLeft, right: marginRight, top: 0, bottom: 0 },
      threshold: OVERLAPPING_THRESHOLD,
      horizontalOffset: HORIZONTAL_OFFSET,
      verticalOffset: VERTICAL_OFFSET,
    })
  }, [data, xScale, yScales, svgWidth, svgHeight, marginLeft, marginRight, theme])
}
